"use client";

import { useState } from "react";
import Link from "next/link";
import {
  UserPlus,
  LogIn,
  Sparkles,
  GraduationCap,
  Wallet,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import {
  APP_SIGNUP_URL,
  APP_STUDENT_URL,
  CLASSE365_PORTAL_URL,
} from "@/client/components/shared";

/**
 * Interactive account cards for the /account page.
 *
 * - Register  → app.theoicnm.com/signup
 * - Login     → reveals two destinations:
 *     • Academic Portal (Classe365)  — classes, grades, academic records
 *     • Student Finance Portal        — tuition/application/custom payments,
 *       invoices, payment history, installment plans, scholarships
 */
export function AccountPortals() {
  const [showLoginOptions, setShowLoginOptions] = useState(false);

  if (showLoginOptions) {
    return (
      <div
        className="max-w-2xl mx-auto"
        style={{ animation: "fadeUp 0.4s ease-out both" }}
      >
        <div className="grid sm:grid-cols-2 gap-6">
          {/* Academic Portal — Classe365 */}
          <Link
            href={CLASSE365_PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open the Academic Portal on Classe365"
            className="group relative rounded-2xl border border-healing-200/60 bg-white/70 backdrop-blur-sm p-8 text-left no-underline shadow-sm hover:shadow-xl hover:border-healing-300 transition-all duration-300 hover:-translate-y-1"
          >
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-healing-500/5 to-nature-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative">
              <div className="w-14 h-14 rounded-xl bg-healing-100 flex items-center justify-center mb-5 group-hover:bg-healing-500 transition-colors duration-300">
                <GraduationCap className="w-7 h-7 text-healing-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h2 className="font-serif-display text-xl font-bold text-healing-900 mb-2">
                Academic Portal
              </h2>
              <p className="text-healing-700/60 text-sm leading-relaxed mb-4">
                Classe365 — access your classes, results, timetable and academic
                records.
              </p>
              <span className="inline-flex items-center gap-1.5 text-healing-600 font-semibold text-sm group-hover:gap-3 transition-all duration-300">
                Open portal
                <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>

          {/* Student Finance Portal — new app */}
          <Link
            href={APP_STUDENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Continue to the Student Finance Portal"
            className="group relative rounded-2xl border border-wisdom-200/60 bg-white/70 backdrop-blur-sm p-8 text-left no-underline shadow-sm hover:shadow-xl hover:border-wisdom-300 transition-all duration-300 hover:-translate-y-1"
          >
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-wisdom-500/5 to-wisdom-300/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative">
              <div className="w-14 h-14 rounded-xl bg-wisdom-100 flex items-center justify-center mb-5 group-hover:bg-wisdom-500 transition-colors duration-300">
                <Wallet className="w-7 h-7 text-wisdom-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h2 className="font-serif-display text-xl font-bold text-healing-900 mb-2">
                Student Finance Portal
              </h2>
              <p className="text-healing-700/60 text-sm leading-relaxed mb-4">
                Pay tuition, application &amp; other fees, view invoices and
                payment history, choose installment plans and apply for
                scholarships.
              </p>
              <span className="inline-flex items-center gap-1.5 text-wisdom-600 font-semibold text-sm group-hover:gap-3 transition-all duration-300">
                Continue to payments
                <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setShowLoginOptions(false)}
          aria-label="Go back to account options"
          className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-healing-700 hover:text-healing-900 transition-colors bg-transparent border-none cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
      {/* Register */}
      <Link
        href={APP_SIGNUP_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Register — create your account"
        className="group relative rounded-2xl border border-healing-200/60 bg-white/70 backdrop-blur-sm p-8 text-left no-underline shadow-sm hover:shadow-xl hover:border-healing-300 transition-all duration-300 hover:-translate-y-1"
        style={{ animation: "fadeUp 0.5s ease-out both" }}
      >
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-healing-500/5 to-nature-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="relative">
          <div className="w-14 h-14 rounded-xl bg-healing-100 flex items-center justify-center mb-5 group-hover:bg-healing-500 transition-colors duration-300">
            <UserPlus className="w-7 h-7 text-healing-600 group-hover:text-white transition-colors duration-300" />
          </div>
          <h2 className="font-serif-display text-2xl font-bold text-healing-900 mb-2">
            Register
          </h2>
          <p className="text-healing-700/60 text-sm leading-relaxed mb-4">
            Begin your journey into naturopathic medicine. Create your account
            and start your application.
          </p>
          <span className="inline-flex items-center gap-1.5 text-healing-600 font-semibold text-sm group-hover:gap-3 transition-all duration-300">
            Get Started
            <Sparkles className="w-4 h-4" />
          </span>
        </div>
      </Link>

      {/* Login — reveals the two portal options */}
      <button
        type="button"
        onClick={() => setShowLoginOptions(true)}
        aria-label="Login — choose your portal"
        className="cursor-pointer group relative rounded-2xl border border-healing-200/60 bg-white/70 backdrop-blur-sm p-8 text-left no-underline shadow-sm hover:shadow-xl hover:border-wisdom-300 transition-all duration-300 hover:-translate-y-1"
        style={{ animation: "fadeUp 0.6s ease-out both" }}
      >
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-wisdom-500/5 to-wisdom-300/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="relative">
          <div className="w-14 h-14 rounded-xl bg-wisdom-100 flex items-center justify-center mb-5 group-hover:bg-wisdom-500 transition-colors duration-300">
            <LogIn className="w-7 h-7 text-wisdom-600 group-hover:text-white transition-colors duration-300" />
          </div>
          <h2 className="font-serif-display text-2xl font-bold text-healing-900 mb-2">
            Login
          </h2>
          <p className="text-healing-700/60 text-sm leading-relaxed mb-4">
            Already a student? Choose your portal — academic records or the
            student finance dashboard.
          </p>
          <span className="inline-flex items-center gap-1.5 text-wisdom-600 font-semibold text-sm group-hover:gap-3 transition-all duration-300">
            Choose portal
            <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </button>
    </div>
  );
}
